import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { withDemoFlag } from './demoMode';

export type RoleDossier = 'commercial' | 'production' | 'administratif' | 'suivi';

export const ROLES_DOSSIER: { value: RoleDossier; label: string; color: string }[] = [
  { value: 'commercial', label: 'Commercial', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  { value: 'production', label: 'Production', color: 'bg-violet-500/15 text-violet-400 border-violet-500/30' },
  { value: 'administratif', label: 'Administratif', color: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
  { value: 'suivi', label: 'Suivi', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
];

export interface IntervenantRow {
  id: string;
  prestation_id: string;
  user_id: string;
  role_dossier: RoleDossier;
  notes: string | null;
  created_at: string;
  // Enrichi côté client
  user_email?: string;
  user_display_name?: string;
}

export interface UserOption {
  user_id: string;
  email: string;
  display_name: string | null;
}

/** Récupère l'UUID DB d'une prestation à partir de son numéro lisible. */
export async function getPrestationUuidByNumero(numero: string): Promise<string | null> {
  const { data } = await supabase.from('prestations').select('id').eq('numero', numero).maybeSingle();
  return (data as any)?.id || null;
}

/** Liste des utilisateurs sélectionnables (depuis profiles). */
export async function listUserOptions(): Promise<UserOption[]> {
  const { data } = await supabase
    .from('profiles')
    .select('user_id, email, display_name')
    .order('display_name', { ascending: true, nullsFirst: false });
  return (data || []) as UserOption[];
}

/** Hook : intervenants d'une prestation (par numero lisible). */
export function useIntervenants(prestNumero: string | null | undefined) {
  const [items, setItems] = useState<IntervenantRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [prestUuid, setPrestUuid] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!prestNumero) { setItems([]); return; }
    setLoading(true);
    const uuid = await getPrestationUuidByNumero(prestNumero);
    setPrestUuid(uuid);
    if (!uuid) { setItems([]); setLoading(false); return; }
    const { data } = await supabase
      .from('prestations_intervenants')
      .select('*')
      .eq('prestation_id', uuid)
      .order('created_at', { ascending: true });
    const rows = (data || []) as IntervenantRow[];
    if (rows.length > 0) {
      const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
      const { data: profs } = await supabase
        .from('profiles')
        .select('user_id, email, display_name')
        .in('user_id', userIds);
      const map = new Map((profs || []).map((p: any) => [p.user_id, p]));
      rows.forEach((r) => {
        const p: any = map.get(r.user_id);
        if (p) { r.user_email = p.email; r.user_display_name = p.display_name; }
      });
    }
    setItems(rows);
    setLoading(false);
  }, [prestNumero]);

  useEffect(() => { load(); }, [load]);

  return { items, loading, prestUuid, reload: load };
}

/** Hook : toutes mes prestations (où je suis intervenant). Retourne la liste des numeros lisibles. */
export function useMesPrestationsNumeros(userId: string | null | undefined) {
  const [numeros, setNumeros] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    async function run() {
      if (!userId) { setNumeros([]); return; }
      setLoading(true);
      const { data: interv } = await supabase
        .from('prestations_intervenants')
        .select('prestation_id')
        .eq('user_id', userId);
      const ids = Array.from(new Set((interv || []).map((r: any) => r.prestation_id)));
      if (ids.length === 0) { if (!cancel) { setNumeros([]); setLoading(false); } return; }
      const { data: prests } = await supabase
        .from('prestations')
        .select('numero')
        .in('id', ids);
      if (!cancel) {
        setNumeros((prests || []).map((p: any) => p.numero).filter(Boolean));
        setLoading(false);
      }
    }
    run();
    return () => { cancel = true; };
  }, [userId]);

  return { numeros, loading };
}

export async function ajouterIntervenant(prestUuid: string, userId: string, role: RoleDossier, notes?: string) {
  const { error } = await supabase.from('prestations_intervenants').insert(withDemoFlag({
    prestation_id: prestUuid,
    user_id: userId,
    role_dossier: role,
    notes: notes || null,
    created_by: (await supabase.auth.getUser()).data.user?.id || null,
  }));
  return error;
}

export async function retirerIntervenant(id: string) {
  const { error } = await supabase.from('prestations_intervenants').delete().eq('id', id);
  return error;
}
